import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import * as XLSX from 'xlsx';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import {
  getTimeZoneOffset,
  getTimeouts,
  getCustomFields,
  getTotals,
  buildSortFn,
  efficiencyGetCustomFieldHeader,
  efficiencyGetTimeoutHeader,
} from './TimeTrackingReportHelpers';
import type { TableRow, SortDirection } from './TimeTrackingReportHelpers';
import './TimeTrackingReportPage.css';
import { useInfoWindow } from '@/contexts/InfoWindowContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number;
  label: string;
}

type SortDir = 'asc' | 'desc';

interface TotalsState {
  productivity: number;
  efficiency: number;
  workingCapacity: number;
  loggedHours: number;
  ptoDays: number;
  productivityHrs: number;
  estimated: number;
  remaining: number;
  actual: number;
  overBudget: number;
}

const emptyTotals: TotalsState = {
  productivity: 0,
  efficiency: 0,
  workingCapacity: 0,
  loggedHours: 0,
  ptoDays: 0,
  productivityHrs: 0,
  estimated: 0,
  remaining: 0,
  actual: 0,
  overBudget: 0,
};

// ─── Custom date input ────────────────────────────────────────────────────────

interface TtrDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}

const TtrDateInput = React.forwardRef<HTMLInputElement, TtrDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="ttr-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="ttr-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="ttr-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
TtrDateInput.displayName = 'TtrDateInput';

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

// ─── Component ───────────────────────────────────────────────────────────────

const TimeTrackingReportPage: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);

  // Filter options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [dashboardOptions, setDashboardOptions] = useState<SelectOption[]>([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<SelectOption[]>([]);
  const [layerOptions, setLayerOptions] = useState<SelectOption[]>([]);
  // All employees from selected teams (for request when no specific employee is selected)
  const [allTeamEmployees, setAllTeamEmployees] = useState<SelectOption[]>([]);

  // Selected filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<SelectOption[]>([]);
  const [selectedCardTypes, setSelectedCardTypes] = useState<SelectOption[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<SelectOption[]>([]);

  // Dates (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromDateEmpty, setFromDateEmpty] = useState(false);
  const [toDateEmpty, setToDateEmpty] = useState(false);
  const [fromDateLess, setFromDateLess] = useState(false);

  // Toggles
  const [includeDisabledUsers, setIncludeDisabledUsers] = useState(false);

  // Table
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [timeoutKeys, setTimeoutKeys] = useState<string[]>([]);
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [loading, setLoading] = useState(false);
  const [resultsFounds, setResultsFounds] = useState(
    'Please select at least a date range to display results.',
  );

  // Summary totals
  const [totals, setTotals] = useState<TotalsState>(emptyTotals);

  // Date range notification
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { openInfoWindow } = useInfoWindow();

  // ── Initial data load ────────────────────────────────────────────────────

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value }))),
      )
      .catch(() => {});

    ReportService.GetAllAsignedTo({
      Teams: '',
      ArtistCategory: '',
      Departments: '',
      GetAll: false,
    } as any)
      .then((data: any[]) =>
      {
        const opts = (data || []).map((e: any) => ({ value: e.PersonID, label: e.Name }));
        setEmployeeOptions(opts);
        setAllTeamEmployees(opts);
      })
      .catch(() => {});

    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
        setProjectOptions(
          (data?.Projects || []).map((p: any) => ({ value: p.ProjectID, label: p.Name })),
        ),
      )
      .catch(() => {});

    ReportService.GetAllCard()
      .then((data: any[]) =>
        setCardTypeOptions((data || []).map(c => ({ value: c.SideMenuId, label: c.Text }))),
      )
      .catch(() => {});

    ReportService.GetLayers()
      .then((data: any[]) =>
        setLayerOptions((data || []).map(l => ({ value: l.LayerId, label: l.Name }))),
      )
      .catch(() => {});
  }, []);

  // ── Cascade loaders ──────────────────────────────────────────────────────

  const loadEmployees = useCallback((teams: number[], inactive: boolean) =>
  {
    ReportService.GetAllAsignedTo({
      Teams: teams.join(','),
      ArtistCategory: '',
      Departments: '',
      GetAll: inactive,
    } as any)
      .then((data: any[]) =>
      {
        const opts = (data || []).map((e: any) => ({ value: e.PersonID, label: e.Name }));
        setEmployeeOptions(opts);
        setAllTeamEmployees(opts);
      })
      .catch(() => {});
  }, []);

  const loadDashboards = useCallback((projectIds: number[]) =>
  {
    if (!projectIds.length)
    {
      setDashboardOptions([]);
      return;
    }
    ReportService.GetDashboardsByProjectIds(projectIds.join(','))
      .then((data: any[]) =>
        setDashboardOptions((data || []).map(d => ({ value: d.DashboardId, label: d.Name }))),
      )
      .catch(() => {});
  }, []);

  // ── Build request ────────────────────────────────────────────────────────

  const buildRequest = useCallback(() =>
  {
    // If teams are selected but no specific employee, send all team employees
    const assignedTo = selectedEmployees.length
      ? selectedEmployees.map(e => e.value).join(',')
      : selectedTeams.length
        ? allTeamEmployees.map(e => e.value).join(',')
        : '';

    return {
      Teams: selectedTeams.map(t => t.value).join(','),
      AsignedTo: assignedTo,
      DateTime: fromDate ? moment(fromDate).format('YYYYMMDD') : '',
      Duedate: toDate ? moment(toDate).format('YYYYMMDD') : '',
      projectId: selectedProjects.map(p => p.value).join(','),
      sideMenuId: selectedCardTypes.map(c => c.value).join(','),
      layersId: selectedLayers.map(l => l.value).join(','),
      dashboardsId: selectedDashboards.map(d => d.value).join(','),
      showGroup: true,
      showAttendanceGroup: false,
      getAll: includeDisabledUsers,
      timeZone: getTimeZoneOffset(),
      comments: '',
      employeeids: '',
      status: '',
      artistCategory: '',
      departments: '',
    };
  }, [
    selectedTeams,
    selectedEmployees,
    allTeamEmployees,
    fromDate,
    toDate,
    selectedProjects,
    selectedCardTypes,
    selectedLayers,
    selectedDashboards,
    includeDisabledUsers,
  ]);

  // ── Fill table ───────────────────────────────────────────────────────────

  const fillTable = useCallback((data: any[]) =>
  {
    if (!data?.length)
    {
      setResultsFounds('No results found');
      setTableData([]);
      setTotals(emptyTotals);
      setTimeoutKeys([]);
      setCustomFieldKeys([]);
      return;
    }
    setResultsFounds('');

    const rows: TableRow[] = [];
    const timeoutsArr: Record<string, string>[] = [];
    const customsArr: Record<string, string>[] = [];
    const seenUsers: number[] = [];
    const countProjects: any[] = [];
    let tWorkedCap = 0,
      tLogged = 0,
      tProdHrs = 0;
    let tStimate = 0,
      tWorked = 0,
      tRemaining = 0,
      tOverBudget = 0;

    for (const item of data)
    {
      const pid = Number(item.PersonId);
      if (!seenUsers.includes(pid))
      {
        seenUsers.push(pid);
        tWorkedCap += Number(item.WorkingCapacity);
      }

      const totalActual = data
        .filter(e => e.CoreTaskId === item.CoreTaskId && e.LayerId === item.LayerId)
        .reduce((s, e) => s + Number(e.Actual), 0);
      (item as any).TotalActual = totalActual;

      if (
        !countProjects.find(e => e.CoreTaskId === item.CoreTaskId && e.LayerId === item.LayerId)
      )
      {
        countProjects.push(item);
      }

      const overbudget = Number(item.Estimated) - (Number(item.Remaining) + Number(item.Actual));
      const alreadyCounted = rows.find(
        r =>
          r.cardname === String(item.CardName) &&
          r.layerName === String(item.LayerName) &&
          r.phaseName === String(item.PhaseName),
      );
      if (!alreadyCounted) tOverBudget += overbudget;

      if (Number(item.LoggedHours) > 0) tLogged += Number(item.LoggedHours);
      if (Number(item.ProductiveHours) > 0) tProdHrs += Number(item.ProductiveHours);

      const to = getTimeouts(item as Record<string, unknown>);
      const cf = getCustomFields(item as Record<string, unknown>);
      timeoutsArr.push(to);
      customsArr.push(cf);

      rows.push({
        id: rows.length + 1,
        employee: String(item.Employee),
        team: String(item.Team),
        milestone: String(item.Milestone ?? ''),
        cardtype: String(item.CardType ?? ''),
        cardname: String(item.CardName ?? ''),
        phaseName: String(item.PhaseName ?? ''),
        layerName: String(item.LayerName ?? ''),
        loggedHours: parseFloat(String(item.LoggedHours)).toFixed(2),
        estimated: parseFloat(String(item.Estimated)).toFixed(2),
        remaining: parseFloat(String(item.Remaining)).toFixed(2),
        actual: parseFloat(String(item.Actual)).toFixed(2),
        burnt: parseFloat(String(overbudget)).toFixed(2),
        conversationID: Number(item.ConversationId),
        timeouts: Object.fromEntries(Object.entries(to).map(([k, v]) => [k, parseFloat(v)])),
        customFields: cf,
      });
    }

    for (const cp of countProjects)
    {
      if (Number(cp.Estimated) > 0) tStimate += Number(cp.Estimated);
      if (Number(cp.Actual) > 0) tWorked += Number(cp.Actual);
      if (Number(cp.Remaining) > 0) tRemaining += Number(cp.Remaining);
    }

    const prod = tWorkedCap > 0 ? Math.min((tProdHrs / tWorkedCap) * 100, 100) : 0;
    const eff = tLogged > 0 ? Math.max((tProdHrs / tLogged) * 100, 0) : 0;

    setTotals({
      productivity: parseFloat(prod.toFixed(2)),
      efficiency: parseFloat(eff.toFixed(2)),
      workingCapacity: parseFloat(tWorkedCap.toFixed(2)),
      loggedHours: parseFloat(tLogged.toFixed(2)),
      ptoDays: Number((data[0] as any).TotalPTODays ?? 0),
      productivityHrs: parseFloat(tProdHrs.toFixed(2)),
      estimated: parseFloat(tStimate.toFixed(2)),
      remaining: parseFloat(tRemaining.toFixed(2)),
      actual: parseFloat(tWorked.toFixed(2)),
      overBudget: parseFloat(tOverBudget.toFixed(2)),
    });

    const toTotals = getTotals(timeoutsArr);
    const cfTotals = getTotals(customsArr);
    setTimeoutKeys(Object.keys(toTotals));
    setCustomFieldKeys(Object.keys(cfTotals));
    setTableData(rows);
  }, []);

  // ── Validation ───────────────────────────────────────────────────────────

  const validate = useCallback((): boolean =>
  {
    let valid = true;
    if (!fromDate)
    {
      setFromDateEmpty(true);
      valid = false;
    }
    else setFromDateEmpty(false);
    if (!toDate)
    {
      setToDateEmpty(true);
      valid = false;
    }
    else setToDateEmpty(false);
    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate)))
    {
      setFromDateLess(true);
      return false;
    }
    setFromDateLess(false);
    if (fromDate && moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return false;
    }
    return valid;
  }, [fromDate, toDate, selectedTeams, selectedEmployees, selectedProjects, selectedCardTypes]);

  // ── Apply filters ────────────────────────────────────────────────────────

  const applyFilters = useCallback(async () =>
  {
    if (!validate()) return;
    setLoading(true);
    setTableData([]);
    setResultsFounds('');
    try
    {
      const req = buildRequest();
      const resp: any[] = await ReportService.GetAllTimeTrack(req);
      fillTable(resp);
    }
    catch (e)
    {
      console.error('TimeTrackingReportPage error:', e);
      setResultsFounds('Error loading data');
    }
    setLoading(false);
  }, [validate, buildRequest, fillTable]);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = (col: string) =>
  {
    const newDir: SortDir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(newDir);
    const sortFn = buildSortFn(col, newDir as SortDirection);
    setTableData(prev => [...prev].sort(sortFn));
  };

  // ── Export ───────────────────────────────────────────────────────────────

  const exportToExcel = () =>
  {
    if (!tableData.length) return;
    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'TimeTracking.xlsx');
  };

  // ── Summary card definitions ─────────────────────────────────────────────

  const summaryCards = [
    {
      label: 'Productivity',
      value: `${totals.productivity}%`,
      tooltip: 'Ability to meet capacity hours of work.',
    },
    {
      label: 'Efficiency',
      value: `${totals.efficiency}%`,
      tooltip: 'Ability to complete a task on time.',
    },
    {
      label: 'Working Capacity',
      value: String(totals.workingCapacity),
      tooltip: 'Total hours a user can work.',
    },
    { label: 'Logged Hours', value: String(totals.loggedHours), tooltip: 'Total captured time.' },
    { label: 'PTO Days', value: String(totals.ptoDays), tooltip: 'Amount of non-available days.' },
    {
      label: 'Productivity Hrs',
      value: String(totals.productivityHrs),
      tooltip: 'Total productive time.',
    },
    {
      label: 'Estimated',
      value: String(totals.estimated),
      tooltip: 'Total sum of estimated values.',
    },
    {
      label: 'Remaining',
      value: String(totals.remaining),
      tooltip: 'Total sum of remaining values.',
    },
    { label: 'Actual', value: String(totals.actual), tooltip: 'Total sum of actual values.' },
    {
      label: 'Over Budget',
      value: String(totals.overBudget),
      tooltip: 'Total sum of over budget values.',
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div id="TimeTrackingReportPage">
        <h3 className="ttr-title">Time Tracking Report</h3>
        <p className="ttr-subtitle">
          This report contains information on the hours logged by the users in their cards as well as the efficiency, total worked, and productivity percentages in the time range selected in the date filters.
          <br></br>
          Dashboard filter will only display information from Default Columns or Dynamic Column, never from both.
        </p>

        <div className="ttr-filters">
          {/* Row 1 */}
          <div className="ttr-filter-row">
            <div className="ttr-filter-item">
              <label className="ttr-label">Team(s):</label>
              <Select
                isMulti
                options={teamOptions}
                value={selectedTeams}
                onChange={v =>
                {
                  const teams = (v as SelectOption[]) || [];
                  setSelectedTeams(teams);
                  setSelectedEmployees([]);
                  loadEmployees(
                    teams.map(t => t.value),
                    includeDisabledUsers,
                  );
                }}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ttr-filter-item">
              <label className="ttr-label">Employee(s):</label>
              <Select
                isMulti
                options={employeeOptions}
                value={selectedEmployees}
                onChange={v => setSelectedEmployees((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ttr-filter-item">
              <label className="ttr-label">Card Type(s):</label>
              <Select
                isMulti
                options={cardTypeOptions}
                value={selectedCardTypes}
                onChange={v => setSelectedCardTypes((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ttr-filter-item">
              <label className="ttr-label">Project(s):</label>
              <Select
                isMulti
                options={projectOptions}
                value={selectedProjects}
                onChange={v =>
                {
                  const projs = (v as SelectOption[]) || [];
                  setSelectedProjects(projs);
                  setSelectedDashboards([]);
                  loadDashboards(projs.map(p => p.value));
                }}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ttr-filter-item">
              <label className="ttr-label">Dashboard(s):</label>
              <Select
                isMulti
                options={dashboardOptions}
                value={selectedDashboards}
                onChange={v => setSelectedDashboards((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ttr-filter-item">
              <label className="ttr-label">Layer(s):</label>
              <Select
                isMulti
                options={layerOptions}
                value={selectedLayers}
                onChange={v => setSelectedLayers((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="ttr-filter-row">
            <div className="ttr-filter-item ttr-filter-item--date">
              <label className="ttr-label">From:</label>
              <DatePicker
                selected={fromDate ? moment(fromDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setFromDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
                customInput={<TtrDateInput onClear={() => setFromDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="ttr-datepicker-calendar"
              />
              {fromDateEmpty && <span className="ttr-validation">Date is required</span>}
              {fromDateLess && (
                <span className="ttr-validation">From date must be less than To date</span>
              )}
            </div>
            <div className="ttr-filter-item ttr-filter-item--date">
              <label className="ttr-label">To:</label>
              <DatePicker
                selected={toDate ? moment(toDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setToDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
                customInput={<TtrDateInput onClear={() => setToDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="ttr-datepicker-calendar"
              />
              {toDateEmpty && <span className="ttr-validation">Date is required</span>}
            </div>
            <div className="ttr-filter-item ttr-filter-item--check">
              <div className="ttr-checkbox-label">
                <span className="corgee_com_checkbox">
                  <input
                    id="ttr-inactive-users-cb"
                    type="checkbox"
                    checked={includeDisabledUsers}
                    onChange={() =>
                    {
                      setIncludeDisabledUsers(prev =>
                      {
                        const next = !prev;
                        loadEmployees(
                          selectedTeams.map(t => t.value),
                          next,
                        );
                        return next;
                      });
                    }}
                  />
                  <label htmlFor="ttr-inactive-users-cb"></label>
                </span>
                <label htmlFor="ttr-inactive-users-cb" className="ttr-checkbox-text">
                  Include Inactive Users
                </label>
              </div>
            </div>
            <div className="ttr-filter-item ttr-filter-item--action">
              <button className="ttr-apply-btn" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="ttr-summary-row">
          {summaryCards.map(({ label, value, tooltip }) => (
            <div key={label} className="ttr-summary-card" title={tooltip}>
              <div className="ttr-summary-value">{value}</div>
              <div className="ttr-summary-label">{label}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="ttr-table-wrapper">
          <div className="ttr-export-row">
            <button
              className="ttr-export-btn"
              onClick={exportToExcel}
              title="Export to Excel"
              disabled={!tableData.length}
            >
              <span className="material-icons style-icon-cloud">cloud_download</span> Export
            </button>
          </div>

          <div className="ttr-table-scroll">
            <table className="ttr-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 36 }}>No.</th>
                  <th onClick={() => handleSort('employee')}>
                    Employee <SortIcon col="employee" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('team')}>
                    Team <SortIcon col="team" sorted={sortCol} dir={sortDir} />
                  </th>
                  {customFieldKeys.map(key => (
                    <th key={key} onClick={() => handleSort(key)}>
                      {efficiencyGetCustomFieldHeader(key)}{' '}
                      <SortIcon col={key} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
                  <th onClick={() => handleSort('project')}>
                    Project <SortIcon col="project" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('card_type')}>
                    Card Type <SortIcon col="card_type" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('card_name')}>
                    Card Name <SortIcon col="card_name" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('phase_name')}>
                    Phase Name <SortIcon col="phase_name" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('layer')}>
                    Layer <SortIcon col="layer" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('logged_hours')}>
                    Logged Hours <SortIcon col="logged_hours" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('estimated')}>
                    Estimated <SortIcon col="estimated" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('remaining')}>
                    Remaining <SortIcon col="remaining" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('actual')}>
                    Actual <SortIcon col="actual" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('over_budget')}>
                    Over Budget <SortIcon col="over_budget" sorted={sortCol} dir={sortDir} />
                  </th>
                  {timeoutKeys.map(key => (
                    <th key={key} onClick={() => handleSort(key)}>
                      {efficiencyGetTimeoutHeader(key)}{' '}
                      <SortIcon col={key} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.id}</td>
                    <td>
                      <span
                        title={row.employee}
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.employee}
                      </span>
                    </td>
                    <td>{row.team}</td>
                    {Object.entries(row.customFields ?? {}).map(([k, v]) => (
                      <td key={k}>{v}</td>
                    ))}
                    <td>
                      <span
                        title={row.milestone}
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.milestone}
                      </span>
                    </td>
                    <td>{row.cardtype}</td>
                    <td>
                      <a
                        style={{ cursor: 'pointer' }}
                        title={row.cardname}
                        onClick={() =>
                        {
                          if (row.conversationID !== undefined)
                          {
                            openInfoWindow(row.conversationID);
                          }
                        }}
                      >
                        {row.cardname}
                      </a>
                    </td>
                    <td>
                      <span
                        title={row.phaseName}
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.phaseName}
                      </span>
                    </td>
                    <td>{row.layerName}</td>
                    <td>{row.loggedHours}</td>
                    <td>{row.estimated}</td>
                    <td>{row.remaining}</td>
                    <td>{row.actual}</td>
                    <td>{row.burnt}</td>
                    {Object.entries(row.timeouts ?? {}).map(([k, v]) => (
                      <td key={k}>{v}</td>
                    ))}
                  </tr>
                ))}
                {!loading && tableData.length === 0 && (
                  <tr>
                    <td colSpan={100} className="ttr-no-data">
                      {resultsFounds}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes ttr-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'ttr-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default TimeTrackingReportPage;
